import { useRef, useState } from "react";
import { Loader2, SendHorizonal } from "lucide-react";

import { Button } from "@/components/ui/button";

interface ComposerProps {
  channelName?: string;
  disabled?: boolean;
  sending?: boolean;
  onSend: (content: string) => void;
}

export function Composer({ channelName, disabled, sending, onSend }: ComposerProps) {
  const [value, setValue] = useState("");
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const submit = () => {
    const content = value.trim();
    if (!content || disabled || sending) return;
    onSend(content);
    setValue("");
    requestAnimationFrame(() => {
      const el = areaRef.current;
      if (el) {
        el.style.height = "auto";
        el.focus();
      }
    });
  };

  return (
    <div className="border-t bg-card p-4">
      <div className="flex items-end gap-2 rounded-xl border bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-ring">
        <textarea
          ref={areaRef}
          value={value}
          rows={1}
          disabled={disabled}
          placeholder={
            channelName ? `Message #${channelName}` : "Message the chama"
          }
          aria-label="Message"
          className="max-h-40 flex-1 resize-none bg-transparent py-1.5 text-[0.95rem] outline-none placeholder:text-muted-foreground disabled:opacity-50"
          onChange={(e) => {
            setValue(e.target.value);
            const el = e.currentTarget;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          size="icon"
          className="size-9 shrink-0"
          disabled={!value.trim() || disabled || sending}
          onClick={submit}
          aria-label="Send message"
        >
          {sending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <SendHorizonal className="size-4" />
          )}
        </Button>
      </div>
      <p className="mt-1.5 px-1 text-xs text-muted-foreground">
        End-to-end encrypted · Enter to send, Shift+Enter for a new line
      </p>
    </div>
  );
}
